/**
 * Draws racers in their current mode with their skin: the cube icon (shared with the shell,
 * rotated GD-style), a ship hull, a rolling ball, a UFO saucer with a glass dome, the wave's
 * dart — the vehicles carry a small copy of the cube —, the robot (the cube on two legs that
 * run, tuck in the air and fire jets while it thrusts) or the spider (the cube on four bent
 * legs that scuttle). Plus the auras of active boosts and, for ghosts, name tags that keep out
 * of each other's way.
 *
 * Cube icons and name tags are pre-drawn sprites (RacerArt), so a racer costs a few draw
 * calls per frame and nothing is allocated.
 */
import {
  avatarEmoji,
  BOOSTS,
  boostBit,
  type Gravity,
  type Mode,
  type PlayerProfile,
} from '@cube-rush/shared';
import { stamp } from './canvas';
import { luminance, rgba } from './color';
import { cubeAccent, skinColor } from './icons';
import { drawCubeSprite, drawTagSprite, glowSprite, SizedSprite } from './sprites';
import type { TrailColors } from './trails';
import type { ViewTransform } from './view';

const INK = '#0a0614';
/**
 * Outline of a nearly black body, and the glow of a skin that is black all over: dark ink
 * would make it vanish on the dark backdrops (the cube icons do the same, see icons.ts).
 */
const LIGHT_INK = '#efe8ff';
/** Colours darker than this (relative luminance) count as nearly black. */
const DARK_LUMINANCE = 0.03;
/** Left and right double-jump wing. */
const WING_SIDES = [-1, 1] as const;
/** Reused dash patterns (setLineDash copies them). */
const DASH = [0, 0];
const NO_DASH: number[] = [];
const TURBO = boostBit('turbo');
const SHIELD = boostBit('shield');
const DOUBLE_JUMP = boostBit('doubleJump');
const SLOWMO = boostBit('slowmo');
/** Name tags stacked at most this high where racers bunch up; the rest wait for room. */
const MAX_TAG_LANES = 4;
const TAG_GAP = 2;
const TAU = Math.PI * 2;
/**
 * Robot: blocks run per step of its legs; from its lean (see the robot's pose in game/pose.ts:
 * upright on the ground, ≥ 0.06 rad either way in the air, −0.22 while its thrust lifts it) how
 * far its legs are tucked and how bright its jets burn.
 */
const ROBOT_STRIDE = 1.15;
/** Robot: length of its thigh and of its shin, blocks. */
const ROBOT_LIMB = 0.3;
const ROBOT_AIR_LEAN = 0.05;
const ROBOT_JETS_FROM = 0.13;
const ROBOT_JETS_SPAN = 0.06;
/** Spider: blocks run per cycle of its legs. */
const SPIDER_STRIDE = 0.8;

/** A racer's look, with its sprites cached at the sizes in use. */
export class RacerArt {
  readonly primary: string;
  readonly secondary: string;
  /** The colour the racer is recognised by (glow, trail); never nearly black. */
  readonly accent: string;
  /** Outline of the vehicles: dark, or light around a nearly black body. */
  readonly ink: string;
  /** Colours of the skin trail. */
  readonly trailColors: TrailColors;
  private readonly cube: SizedSprite;
  private readonly tag: SizedSprite;

  constructor(readonly profile: PlayerProfile) {
    this.primary = skinColor(profile.skin.primary);
    this.secondary = skinColor(profile.skin.secondary);
    const accent = cubeAccent(profile.skin);
    this.accent = luminance(accent) < DARK_LUMINANCE ? LIGHT_INK : accent;
    this.ink = luminance(this.primary) < DARK_LUMINANCE ? LIGHT_INK : INK;
    const second = luminance(this.secondary) < DARK_LUMINANCE ? LIGHT_INK : this.secondary;
    this.trailColors = { primary: this.accent, secondary: second };
    this.cube = new SizedSprite((size) => drawCubeSprite(profile.skin, size));
    const emoji = avatarEmoji(profile.avatar);
    this.tag = new SizedSprite((size) => drawTagSprite(emoji, profile.name, size));
  }

  /** True if this art still shows `profile` (otherwise make a new one). */
  matches(profile: PlayerProfile): boolean {
    const a = this.profile;
    return (
      a === profile ||
      (a.name === profile.name &&
        a.avatar === profile.avatar &&
        a.skin.icon === profile.skin.icon &&
        a.skin.primary === profile.skin.primary &&
        a.skin.secondary === profile.skin.secondary &&
        a.skin.trail === profile.skin.trail)
    );
  }

  cubeSprite(devicePx: number): HTMLCanvasElement | null {
    return this.cube.get(devicePx);
  }

  tagSprite(devicePx: number): HTMLCanvasElement | null {
    return this.tag.get(devicePx);
  }
}

/** One racer in one frame. */
export interface RacerFrame {
  x: number;
  y: number;
  rot: number;
  mode: Mode;
  gravity: Gravity;
  alive: boolean;
  /** Active boosts (PlayerNetState.fx bits). */
  fx: number;
}

export interface RacerDrawOptions {
  /** 1 = solid; ghosts are translucent. */
  alpha: number;
  /** Neon glow behind the racer. */
  glow: boolean;
}

export function drawRacer(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  scale: number,
  art: RacerArt,
  r: RacerFrame,
  time: number,
  options: RacerDrawOptions,
): void {
  if (!r.alive) return;
  const s = view.ppb;
  const cx = view.sx(r.x);
  const cy = view.sy(r.y);
  if (!nearView(view, cx, cy)) return;
  ctx.globalAlpha = options.alpha;

  if (options.glow) {
    const glow = glowSprite(art.accent);
    if (glow) {
      const size = s * 2.3;
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = 0.42 * options.alpha;
      ctx.drawImage(glow, cx - size / 2, cy - size / 2, size, size);
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = options.alpha;
    }
  }
  drawAurasBehind(ctx, cx, cy, s, r.fx, time, options.alpha);

  const flip = r.gravity < 0;
  switch (r.mode) {
    case 'cube': {
      const sprite = art.cubeSprite(s * scale);
      if (sprite) stamp(ctx, scale, sprite, cx, cy, s * 1.02, s * 1.02, r.rot, flip);
      break;
    }
    case 'ship':
      drawShip(ctx, scale, art, cx, cy, s, r.rot, flip, time);
      break;
    case 'ball':
      drawBall(ctx, scale, art, cx, cy, s, r.rot);
      break;
    case 'ufo':
      drawUfo(ctx, scale, art, cx, cy, s, r.rot, flip, time);
      break;
    case 'wave':
      drawWave(ctx, scale, art, cx, cy, s, r.rot, flip);
      break;
    case 'robot':
      drawRobot(ctx, scale, art, r, cx, cy, s, flip, time);
      break;
    case 'spider':
      drawSpider(ctx, scale, art, r, cx, cy, s, flip);
      break;
  }
  drawAurasFront(ctx, cx, cy, s, r.fx, time, options.alpha);
  ctx.globalAlpha = 1;
}

/**
 * Where the name tags of one view go this frame: a tag that would cover one placed before it
 * moves up a lane, so racers bunched together (at the start, say) all stay readable; with
 * every lane taken it is left out. Fixed storage, nothing allocated per frame.
 */
export class TagLayout {
  private readonly boxes = new Float64Array(4 * 16);
  private count = 0;

  clear(): void {
    this.count = 0;
  }

  /** The top for a tag of w × h wanted at (x, y) (its top-left), or null if there is no room. */
  place(x: number, y: number, w: number, h: number): number | null {
    for (let lane = 0; lane < MAX_TAG_LANES; lane++) {
      const top = y - lane * (h + TAG_GAP);
      if (this.overlaps(x, top, w, h)) continue;
      if (this.count * 4 < this.boxes.length) {
        const k = this.count++ * 4;
        this.boxes[k] = x;
        this.boxes[k + 1] = top;
        this.boxes[k + 2] = x + w;
        this.boxes[k + 3] = top + h;
      }
      return top;
    }
    return null;
  }

  private overlaps(x: number, top: number, w: number, h: number): boolean {
    const b = this.boxes;
    for (let k = 0; k < this.count * 4; k += 4) {
      if (x < b[k + 2]! && x + w > b[k]! && top < b[k + 3]! && top + h > b[k + 1]!) return true;
    }
    return false;
  }
}

/** A ghost's name tag (avatar emoji + name) above it, placed by `layout`. */
export function drawRacerTag(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  scale: number,
  art: RacerArt,
  r: RacerFrame,
  alpha: number,
  layout: TagLayout,
): void {
  if (!r.alive) return;
  const s = view.ppb;
  const cx = view.sx(r.x);
  const cy = view.sy(r.y);
  if (!nearView(view, cx, cy)) return;
  const height = Math.min(26, Math.max(15, s * 0.42));
  const tag = art.tagSprite(height * scale);
  if (!tag) return;
  const w = (tag.width / tag.height) * height;
  const lift = r.mode === 'wave' ? 0.65 : 1.05;
  const top = layout.place(cx - w / 2, cy - s * lift - height, w, height);
  if (top === null) return;
  ctx.globalAlpha = Math.min(1, alpha + 0.35);
  ctx.drawImage(tag, cx - w / 2, top, w, height);
  ctx.globalAlpha = 1;
}

/** Is a racer centred at (cx, cy) on screen, or close enough for its glow to show? */
function nearView(view: ViewTransform, cx: number, cy: number): boolean {
  const margin = view.ppb * 2;
  return (
    cx >= view.x - margin &&
    cx <= view.x + view.w + margin &&
    cy >= view.y - margin &&
    cy <= view.y + view.h + margin
  );
}

/** Sets a rotated (and, for reversed gravity, mirrored) transform centred on (cx, cy). */
function local(
  ctx: CanvasRenderingContext2D,
  scale: number,
  cx: number,
  cy: number,
  rot: number,
  flip: boolean,
): void {
  const cos = Math.cos(rot) * scale;
  const sin = Math.sin(rot) * scale;
  const f = flip ? -1 : 1;
  ctx.setTransform(cos, sin, -sin * f, cos * f, cx * scale, cy * scale);
}

function reset(ctx: CanvasRenderingContext2D, scale: number): void {
  ctx.setTransform(scale, 0, 0, scale, 0, 0);
}

function drawShip(
  ctx: CanvasRenderingContext2D,
  scale: number,
  art: RacerArt,
  cx: number,
  cy: number,
  s: number,
  rot: number,
  flip: boolean,
  time: number,
): void {
  local(ctx, scale, cx, cy, rot, flip);
  // Flame.
  const flicker = 0.75 + Math.sin(time * 40) * 0.15 + Math.sin(time * 23) * 0.1;
  ctx.fillStyle = '#ffb238';
  ctx.beginPath();
  ctx.moveTo(-0.68 * s, 0.06 * s);
  ctx.lineTo(-(0.68 + 0.42 * flicker) * s, 0.18 * s);
  ctx.lineTo(-0.68 * s, 0.32 * s);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#fff27a';
  ctx.beginPath();
  ctx.moveTo(-0.68 * s, 0.12 * s);
  ctx.lineTo(-(0.68 + 0.22 * flicker) * s, 0.19 * s);
  ctx.lineTo(-0.68 * s, 0.26 * s);
  ctx.closePath();
  ctx.fill();
  // The cube rides in the cockpit.
  const sprite = art.cubeSprite(s * 0.56 * scale);
  if (sprite) ctx.drawImage(sprite, -0.36 * s, -0.62 * s, 0.52 * s, 0.52 * s);
  // Hull.
  ctx.beginPath();
  ctx.moveTo(-0.72 * s, 0.02 * s);
  ctx.lineTo(-0.82 * s, -0.3 * s);
  ctx.lineTo(-0.5 * s, -0.14 * s);
  ctx.lineTo(0.2 * s, -0.14 * s);
  ctx.lineTo(0.78 * s, 0.14 * s);
  ctx.lineTo(0.42 * s, 0.4 * s);
  ctx.lineTo(-0.62 * s, 0.4 * s);
  ctx.closePath();
  ctx.fillStyle = art.primary;
  ctx.fill();
  ctx.lineJoin = 'round';
  ctx.lineWidth = Math.max(1.5, s * 0.07);
  ctx.strokeStyle = art.ink;
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(-0.5 * s, 0.14 * s);
  ctx.lineTo(0.42 * s, 0.14 * s);
  ctx.lineTo(0.3 * s, 0.26 * s);
  ctx.lineTo(-0.5 * s, 0.26 * s);
  ctx.closePath();
  ctx.fillStyle = art.secondary;
  ctx.fill();
  ctx.lineWidth = Math.max(1, s * 0.04);
  ctx.strokeStyle = INK;
  ctx.stroke();
  reset(ctx, scale);
}

function drawBall(
  ctx: CanvasRenderingContext2D,
  scale: number,
  art: RacerArt,
  cx: number,
  cy: number,
  s: number,
  rot: number,
): void {
  const r = s * 0.5;
  local(ctx, scale, cx, cy, rot, false);
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.94, 0, Math.PI * 2);
  ctx.fillStyle = art.primary;
  ctx.fill();
  // Two opposite segments in the second colour show the rolling.
  ctx.fillStyle = art.secondary;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.arc(0, 0, r * 0.94, -0.45, 0.45);
  ctx.closePath();
  ctx.moveTo(0, 0);
  ctx.arc(0, 0, r * 0.94, Math.PI - 0.45, Math.PI + 0.45);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.32, 0, Math.PI * 2);
  ctx.fillStyle = art.primary;
  ctx.fill();
  ctx.lineWidth = Math.max(1, s * 0.05);
  ctx.strokeStyle = INK;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.94, 0, Math.PI * 2);
  ctx.lineWidth = Math.max(1.5, s * 0.08);
  ctx.strokeStyle = art.ink;
  ctx.stroke();
  reset(ctx, scale);
  // Shine does not roll.
  ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
  ctx.beginPath();
  ctx.ellipse(cx - r * 0.35, cy - r * 0.4, r * 0.22, r * 0.12, -0.6, 0, Math.PI * 2);
  ctx.fill();
}

function drawUfo(
  ctx: CanvasRenderingContext2D,
  scale: number,
  art: RacerArt,
  cx: number,
  cy: number,
  s: number,
  rot: number,
  flip: boolean,
  time: number,
): void {
  local(ctx, scale, cx, cy, rot, flip);
  // Glass dome with the cube inside.
  const sprite = art.cubeSprite(s * 0.46 * scale);
  if (sprite) ctx.drawImage(sprite, -0.22 * s, -0.36 * s, 0.44 * s, 0.44 * s);
  ctx.beginPath();
  ctx.arc(0, 0.04 * s, 0.36 * s, Math.PI, 0);
  ctx.closePath();
  ctx.fillStyle = 'rgba(200, 240, 255, 0.28)';
  ctx.fill();
  ctx.lineWidth = Math.max(1.5, s * 0.055);
  ctx.strokeStyle = art.ink;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(-0.12 * s, -0.12 * s, 0.1 * s, Math.PI, Math.PI * 1.6);
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
  ctx.lineWidth = Math.max(1, s * 0.04);
  ctx.stroke();
  // Saucer.
  ctx.beginPath();
  ctx.ellipse(0, 0.16 * s, 0.66 * s, 0.2 * s, 0, 0, Math.PI * 2);
  ctx.fillStyle = art.primary;
  ctx.fill();
  ctx.lineWidth = Math.max(1.5, s * 0.07);
  ctx.strokeStyle = art.ink;
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(0, 0.24 * s, 0.4 * s, 0.08 * s, 0, 0, Math.PI);
  ctx.fillStyle = art.secondary;
  ctx.fill();
  // Blinking rim lights.
  for (let k = 0; k < 3; k++) {
    const on = Math.sin(time * 8 + k * 2.1) > 0;
    ctx.fillStyle = on ? '#ffffff' : art.secondary;
    ctx.beginPath();
    ctx.arc((k - 1) * 0.32 * s, 0.15 * s, 0.055 * s, 0, Math.PI * 2);
    ctx.fill();
  }
  reset(ctx, scale);
}

function drawWave(
  ctx: CanvasRenderingContext2D,
  scale: number,
  art: RacerArt,
  cx: number,
  cy: number,
  s: number,
  rot: number,
  flip: boolean,
): void {
  local(ctx, scale, cx, cy, rot, flip);
  ctx.beginPath();
  ctx.moveTo(0.44 * s, 0);
  ctx.lineTo(-0.36 * s, -0.3 * s);
  ctx.lineTo(-0.2 * s, 0);
  ctx.lineTo(-0.36 * s, 0.3 * s);
  ctx.closePath();
  ctx.fillStyle = art.primary;
  ctx.fill();
  ctx.lineJoin = 'round';
  ctx.lineWidth = Math.max(1.5, s * 0.07);
  ctx.strokeStyle = art.ink;
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(0.22 * s, 0);
  ctx.lineTo(-0.16 * s, -0.13 * s);
  ctx.lineTo(-0.08 * s, 0);
  ctx.lineTo(-0.16 * s, 0.13 * s);
  ctx.closePath();
  ctx.fillStyle = art.secondary;
  ctx.fill();
  reset(ctx, scale);
}

/**
 * The robot: its legs run with the distance it covers and tuck in the air, its jets burn while
 * its thrust lifts it (both read from its lean, see ROBOT_AIR_LEAN), the cube on top.
 */
function drawRobot(
  ctx: CanvasRenderingContext2D,
  scale: number,
  art: RacerArt,
  r: RacerFrame,
  cx: number,
  cy: number,
  s: number,
  flip: boolean,
  time: number,
): void {
  const air = Math.min(1, Math.abs(r.rot) / ROBOT_AIR_LEAN);
  const jets = Math.min(1, Math.max(0, (-r.rot - ROBOT_JETS_FROM) / ROBOT_JETS_SPAN));
  local(ctx, scale, cx, cy, r.rot, flip);
  // Two legs half a step apart, the far one first: a thigh and a shin bending at the knee (in
  // front), a boot at the foot, a flame under it while the thrust lasts.
  const phase = (r.x / ROBOT_STRIDE) * TAU;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (let leg = 0; leg < 2; leg++) {
    const p = phase + leg * Math.PI;
    const hipX = (leg === 0 ? -0.1 : 0.1) * s;
    const hipY = -0.08 * s;
    const footX = lerp(0.24 * Math.sin(p), leg === 0 ? -0.12 : 0.14, air) * s;
    const footY = lerp(0.44 - 0.14 * Math.max(0, Math.cos(p)), 0.32, air) * s;
    // The knee where two segments of ROBOT_LIMB reach from hip to foot, bent forward.
    const dx = footX - hipX;
    const dy = footY - hipY;
    const d = Math.sqrt(dx * dx + dy * dy);
    const limb = ROBOT_LIMB * s;
    const bend = d < 2 * limb ? Math.sqrt(limb * limb - (d * d) / 4) / Math.max(d, 1e-6) : 0;
    const kneeX = (hipX + footX) / 2 + dy * bend;
    const kneeY = (hipY + footY) / 2 - dx * bend;
    if (jets > 0) {
      const flicker = 0.8 + Math.sin(time * 47 + leg) * 0.12 + Math.sin(time * 29) * 0.08;
      const length = 0.4 * jets * flicker * s;
      const top = footY + 0.04 * s;
      ctx.fillStyle = '#ffb238';
      ctx.beginPath();
      ctx.moveTo(footX - 0.09 * s, top);
      ctx.lineTo(footX, top + length);
      ctx.lineTo(footX + 0.09 * s, top);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#fff27a';
      ctx.beginPath();
      ctx.moveTo(footX - 0.045 * s, top);
      ctx.lineTo(footX, top + length * 0.55);
      ctx.lineTo(footX + 0.045 * s, top);
      ctx.closePath();
      ctx.fill();
    }
    const color = leg === 0 ? art.primary : art.secondary;
    ctx.beginPath();
    ctx.moveTo(hipX, hipY);
    ctx.lineTo(kneeX, kneeY);
    ctx.lineTo(footX, footY);
    ctx.strokeStyle = art.ink;
    ctx.lineWidth = Math.max(3.5, s * 0.2);
    ctx.stroke();
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(2, s * 0.11);
    ctx.stroke();
    // A round joint at the knee, a boot (its toe forward) at the foot.
    ctx.beginPath();
    ctx.arc(kneeX, kneeY, Math.max(2, s * 0.075), 0, TAU);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = Math.max(1, s * 0.045);
    ctx.strokeStyle = art.ink;
    ctx.stroke();
    ctx.fillStyle = art.ink;
    ctx.fillRect(footX - 0.1 * s, footY - 0.03 * s, 0.27 * s, 0.09 * s);
  }
  ctx.lineCap = 'butt';
  // The cube rides on its legs.
  const sprite = art.cubeSprite(s * 0.6 * scale);
  if (sprite) ctx.drawImage(sprite, -0.28 * s, -0.62 * s, 0.6 * s, 0.6 * s);
  reset(ctx, scale);
}

/** The spider: the cube between four bent legs, two a side, scuttling as it runs. */
function drawSpider(
  ctx: CanvasRenderingContext2D,
  scale: number,
  art: RacerArt,
  r: RacerFrame,
  cx: number,
  cy: number,
  s: number,
  flip: boolean,
): void {
  local(ctx, scale, cx, cy, r.rot, flip);
  const phase = (r.x / SPIDER_STRIDE) * TAU;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (let leg = 0; leg < 4; leg++) {
    // Front pair (right) and back pair (left), each an outer leg arching wide over the body and
    // a shorter inner one; they step in turn, a quarter cycle apart.
    const side = leg < 2 ? 1 : -1;
    const outer = leg % 2 === 0 ? 1 : 0;
    const p = phase + leg * (Math.PI / 2);
    const lift = Math.max(0, Math.sin(p)) * 0.1;
    const swing = Math.cos(p) * 0.07;
    const hipX = side * 0.16 * s;
    const hipY = 0;
    const kneeX = (side * (0.4 + 0.2 * outer) + swing * 0.5) * s;
    const kneeY = (-0.2 - 0.12 * outer - lift * 0.5) * s;
    const footX = (side * (0.52 + 0.24 * outer) + swing) * s;
    const footY = (0.5 - lift) * s;
    ctx.beginPath();
    ctx.moveTo(hipX, hipY);
    ctx.lineTo(kneeX, kneeY);
    ctx.lineTo(footX, footY);
    ctx.strokeStyle = art.ink;
    ctx.lineWidth = Math.max(2.5, s * 0.12);
    ctx.stroke();
    ctx.strokeStyle = outer === 1 ? art.primary : art.secondary;
    ctx.lineWidth = Math.max(1, s * 0.05);
    ctx.stroke();
  }
  ctx.lineCap = 'butt';
  // The cube sits low between its knees.
  const sprite = art.cubeSprite(s * 0.62 * scale);
  if (sprite) ctx.drawImage(sprite, -0.31 * s, -0.3 * s, 0.62 * s, 0.62 * s);
  reset(ctx, scale);
}

function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}

/** Boost effects behind the character: turbo glow, slow-motion halo. */
function drawAurasBehind(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  s: number,
  fx: number,
  time: number,
  alpha: number,
): void {
  if (fx === 0) return;
  if (fx & TURBO) {
    const glow = glowSprite(BOOSTS.turbo.color);
    if (glow) {
      const size = s * (2.6 + Math.sin(time * 18) * 0.2);
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = 0.7 * alpha;
      ctx.drawImage(glow, cx - size * 0.75, cy - size / 2, size, size);
      ctx.globalCompositeOperation = 'source-over';
    }
    // Speed streaks trailing behind.
    ctx.strokeStyle = rgba(BOOSTS.turbo.color, 0.75);
    ctx.lineWidth = Math.max(1.5, s * 0.06);
    ctx.beginPath();
    for (let k = 0; k < 3; k++) {
      const y = cy + (k - 1) * s * 0.3;
      const len = s * (0.9 + ((time * 7 + k * 0.37) % 1) * 0.7);
      ctx.moveTo(cx - s * 0.65, y);
      ctx.lineTo(cx - s * 0.65 - len, y);
    }
    ctx.stroke();
  }
  if (fx & SLOWMO) {
    ctx.globalAlpha = 0.6 * alpha;
    ctx.strokeStyle = BOOSTS.slowmo.color;
    ctx.lineWidth = Math.max(1.5, s * 0.06);
    const r = s * (0.95 + Math.sin(time * 2.5) * 0.08);
    DASH[0] = s * 0.18;
    DASH[1] = s * 0.14;
    ctx.setLineDash(DASH);
    ctx.lineDashOffset = time * s * 0.3;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash(NO_DASH);
  }
  ctx.globalAlpha = alpha;
}

/** Boost effects in front: the shield bubble, double-jump wings. */
function drawAurasFront(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  s: number,
  fx: number,
  time: number,
  alpha: number,
): void {
  if (fx === 0) return;
  if (fx & SHIELD) {
    const r = s * (0.86 + Math.sin(time * 9) * 0.04);
    ctx.globalAlpha = alpha;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = rgba(BOOSTS.shield.color, 0.16);
    ctx.fill();
    ctx.strokeStyle = rgba(BOOSTS.shield.color, 0.9);
    ctx.lineWidth = Math.max(1.5, s * 0.07);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.78, Math.PI * 1.1, Math.PI * 1.45);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.lineWidth = Math.max(1.5, s * 0.06);
    ctx.lineCap = 'round';
    ctx.stroke();
    ctx.lineCap = 'butt';
  }
  if (fx & DOUBLE_JUMP) {
    const flap = Math.sin(time * 14) * 0.18;
    ctx.globalAlpha = alpha;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
    ctx.strokeStyle = BOOSTS.doubleJump.color;
    ctx.lineWidth = Math.max(1.5, s * 0.05);
    for (const side of WING_SIDES) {
      const bx = cx + side * s * 0.5;
      const by = cy - s * 0.1;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.quadraticCurveTo(
        bx + side * s * 0.45,
        by - s * (0.55 + flap),
        bx + side * s * 0.6,
        by - s * (0.2 + flap),
      );
      ctx.quadraticCurveTo(bx + side * s * 0.4, by - s * 0.05, bx, by + s * 0.12);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
  }
  ctx.globalAlpha = alpha;
}
